import { Spin } from 'antd';
import { max, debounce } from 'lodash';
import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
  useLayoutEffect,
  useEffect,
} from 'react';
import styles from './tableContext.module.less';

type Props = PropsWithChildren<{}>;

type TableData = {
  rowData: {
    [key: string]: {
      height: {
        [key: number]: number;
      };
    };
  };
};

type TableContextType = {
  value: TableData;
  setValue: React.Dispatch<React.SetStateAction<TableData>>;
  getMaxHeight: (fieldName: string) => number;
  reset: () => void;
};

const initialState = {
  rowData: {},
};

export const TableContext = createContext<TableContextType>({
  value: initialState,
  setValue: () => {},
  getMaxHeight: () => -1,
  reset: () => {},
});

const TableContextProvider = ({ children }: Props) => {
  const [tableContext, setTableContext] = useState<TableData>(initialState);

  const [isResizing, setIsResizing] = useState(false);

  const prevWidthRef = useRef<number | null>(null);

  const getMaxHeight = (fieldName: string) => {
    const rowData = tableContext.rowData[fieldName];
    return max(Object.values(rowData?.height || [])) || -1;
  };

  const reset = () => {
    setTableContext(initialState);
  };

  // On iOS Safari, the resize event is triggered on scroll (due to browser chrome),
  // but true layout change only happens when width changes.
  // Only reset if width has changed.
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const handleResize = debounce(() => {
      const newWidth = window.innerWidth;
      const prevWidth = prevWidthRef.current;

      if (prevWidth !== null && prevWidth !== newWidth) {
        reset();
        timeoutId = setTimeout(() => {
          setIsResizing(false);
        }, 200);
      }
      prevWidthRef.current = newWidth;
    }, 200);

    const updateIsResizing = () => {
      // Only set to resizing if width actually changes
      const newWidth = window.innerWidth;
      const prevWidth = prevWidthRef.current;
      if (prevWidth !== null && prevWidth !== newWidth) {
        setIsResizing(true);
      }
    };

    prevWidthRef.current = window.innerWidth;

    window.addEventListener('resize', updateIsResizing);
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('resize', updateIsResizing);
    };
  }, []);

  return (
    <TableContext.Provider
      value={{
        value: tableContext,
        setValue: setTableContext,
        getMaxHeight,
        reset,
      }}
    >
      {isResizing ? (
        <div className={styles.spinContainer}>
          <Spin size="large" />
        </div>
      ) : (
        children
      )}
    </TableContext.Provider>
  );
};

export const useTableHeightSync = (fieldName: string, idx: number = -1) => {
  const { value, setValue } = useContext(TableContext);
  const rowData = value.rowData[fieldName];
  const ref = useRef<any>(null);

  useLayoutEffect(() => {
    if (ref?.current?.clientHeight && !rowData?.height[idx]) {
      setValue((prevValue: TableData) => {
        const rowData = prevValue.rowData[fieldName];
        return {
          ...prevValue,
          rowData: {
            ...prevValue.rowData,
            [fieldName]: {
              height: {
                ...(rowData?.height || {}),
                [idx]: ref?.current?.clientHeight,
              },
            },
          },
        };
      });
    }
  }, [fieldName, setValue, ref?.current?.clientHeight, idx, rowData]);

  const maxHeight = max(Object.values(rowData?.height || {}));

  // Always return maxHeight if any plan has measured this field
  // This ensures consistent height across all plans, even when field is missing in some plans
  const shouldUseMaxHeight = maxHeight && maxHeight > 0;

  return {
    ref,
    height: shouldUseMaxHeight ? maxHeight : undefined,
  };
};

export const useMultiTableHeightSync = (
  fieldNames: string[],
  idx: number = -1
) => {
  const { value, setValue } = useContext(TableContext);
  const refs = useRef<{ [key: string]: HTMLElement | null }>({});

  // Callback ref factory for each field
  const getRef = useCallback(
    (fieldName: string) => {
      return (element: HTMLElement | null) => {
        refs.current[fieldName] = element;

        if (element?.clientHeight) {
          const currentRowData = value.rowData[fieldName];

          if (currentRowData?.height?.[idx] === undefined) {
            setValue((prevValue: TableData) => {
              const rowData = prevValue.rowData[fieldName];
              return {
                ...prevValue,
                rowData: {
                  ...prevValue.rowData,
                  [fieldName]: {
                    height: {
                      ...(rowData?.height || {}),
                      [idx]: element.clientHeight,
                    },
                  },
                },
              };
            });
          }
        }
      };
    },
    [setValue, value.rowData, idx]
  );

  // Calculate heights for all fields
  const heights = useMemo(() => {
    const result: { [key: string]: number | undefined } = {};

    fieldNames.forEach((fieldName) => {
      const rowData = value.rowData[fieldName];
      const maxHeight = max(Object.values(rowData?.height || {}));
      // Always use maxHeight if any plan has measured this field
      const shouldUseMaxHeight = maxHeight && maxHeight > 0;
      result[fieldName] = shouldUseMaxHeight ? maxHeight : undefined;
    });

    return result;
  }, [fieldNames, value.rowData]);

  // Return refs and heights for each field
  const refCallbacks = useMemo(() => {
    const result: { [key: string]: (element: HTMLElement | null) => void } = {};
    fieldNames.forEach((fieldName) => {
      result[fieldName] = getRef(fieldName);
    });
    return result;
  }, [fieldNames, getRef]);

  return {
    refs: refCallbacks,
    heights,
  };
};

export default TableContextProvider;
